import { CSSProperties, useState, useCallback } from 'react';
import { PlayCircleIcon, XIcon } from '@phosphor-icons/react';
import { Button } from 'antd';
import styled from '@emotion/styled';
import VideoPreviewModal from './VideoPreviewModal';

interface VideoThumbnailProps {
  fileUrl: string;
  fileName?: string;
  thumbnailUrl?: string;
  mediaStyles: CSSProperties;
  isDragging: boolean;
  onDelete?: () => void;
  isStreaming?: boolean;
  onPreview?: () => void;
}

// Emotion styles
const ThumbnailContainer = styled.div<{ isDragging: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: ${props => (props.isDragging ? 'grabbing' : 'pointer')};
  border-radius: 4px;
  overflow: visible;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
`;

const ThumbnailImage = styled.img<{ mediaStyles: CSSProperties }>`
  ${props => ({ ...props.mediaStyles })}
  background-color: #f8f9fa;
`;

const StreamingPlaceholder = styled.div<{ mediaStyles: CSSProperties }>`
  ${props => ({ ...props.mediaStyles })}
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const PlayIcon = styled(PlayCircleIcon)`
  font-size: 32px;
  color: #6366f1;
  margin-bottom: 8px;
`;

const VideoElement = styled.video<{ mediaStyles: CSSProperties }>`
  ${props => ({ ...props.mediaStyles })}
  background-color: #f8f9fa;
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.3) 100%);
  pointer-events: none;
`;

const PlayButtonOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 28px;
  color: white;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  pointer-events: none;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DeleteButton = styled(Button)`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 22px !important;
  height: 22px !important;
  border-radius: 50% !important;
  background-color: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 11px !important;
  padding: 0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  z-index: 15;
  transition: all 0.2s ease-in-out !important;
  color: rgba(0, 0, 0, 0.7) !important;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8) !important;
    border-color: rgba(0, 0, 0, 0.8) !important;
    transform: scale(1.1);
    color: white !important;
  }

  .anticon {
    color: inherit !important;
  }
`;

const VideoThumbnail = ({
  fileUrl,
  fileName,
  thumbnailUrl,
  mediaStyles,
  isDragging,
  onDelete,
  isStreaming = false,
  onPreview,
}: VideoThumbnailProps) => {
  const [videoPreviewVisible, setVideoPreviewVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  const handleVideoPreview = useCallback(() => {
    if (!isDragging) {
      if (onPreview) onPreview();
      else setVideoPreviewVisible(true);
    }
  }, [isDragging, onPreview]);

  const handleVideoModalClose = useCallback(() => {
    setVideoPreviewVisible(false);
  }, []);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const generateCloudflareStreamThumbnail = (videoUrl: string): string | null => {
    const cloudflareStreamRegex =
      /https:\/\/([^/]+)\.cloudflarestream\.com\/([a-f0-9]{32})\/manifest\/video\.m3u8/i;
    const match = videoUrl.match(cloudflareStreamRegex);

    if (match) {
      const [, domain, videoId] = match;
      return `https://${domain}.cloudflarestream.com/${videoId}/thumbnails/thumbnail.gif?time=1s&height=270`;
    }

    return null;
  };

  const isCloudflareStreamUrl = (url: string): boolean => {
    return /cloudflarestream\.com.*\/manifest\/video\.m3u8/i.test(url);
  };

  const renderVideoContent = () => {
    if (thumbnailUrl)
      return (
        <ThumbnailImage
          src={thumbnailUrl}
          mediaStyles={mediaStyles}
          alt={fileName || 'Video thumbnail'}
        />
      );
    if (isStreaming || isCloudflareStreamUrl(fileUrl)) {
      const thumbnailUrl = generateCloudflareStreamThumbnail(fileUrl);

      if (thumbnailUrl) {
        return (
          <ThumbnailImage
            src={thumbnailUrl}
            mediaStyles={mediaStyles}
            alt={fileName || 'Video thumbnail'}
            onError={e => {
              // Fallback to placeholder if thumbnail fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.parentElement?.appendChild(
                Object.assign(document.createElement('div'), {
                  innerHTML: `
                    <div style="width: 100%; height: 100%; background-color: #f8f9fa; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                      <svg width="32" height="32" fill="#6366f1" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  `,
                }),
              );
            }}
          />
        );
      } else {
        // Fallback for non-Cloudflare streaming videos
        return (
          <StreamingPlaceholder mediaStyles={mediaStyles}>
            <PlayIcon />
          </StreamingPlaceholder>
        );
      }
    } else {
      // Regular video file
      return (
        <VideoElement
          src={fileUrl}
          mediaStyles={mediaStyles}
          muted
          preload="metadata"
          title={fileName}
        />
      );
    }
  };

  return (
    <>
      <ThumbnailContainer
        role="button"
        tabIndex={0}
        aria-label={`Play ${fileName || 'video'}`}
        isDragging={isDragging}
        onClick={handleVideoPreview}
        onKeyDown={event => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleVideoPreview();
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <VideoContainer>
          {renderVideoContent()}
          <VideoOverlay />

          <PlayButtonOverlay>
            <PlayCircleIcon />
          </PlayButtonOverlay>
        </VideoContainer>

        {onDelete && isHovered && (
          <DeleteButton type="text" size="small" icon={<XIcon />} onClick={handleDeleteClick} />
        )}
      </ThumbnailContainer>

      {/* Video Preview Modal */}
      {!onPreview && videoPreviewVisible && (
        <VideoPreviewModal
          visible={videoPreviewVisible}
          fileUrl={fileUrl}
          fileName={fileName}
          onClose={handleVideoModalClose}
          isStreaming={isStreaming || isCloudflareStreamUrl(fileUrl)}
        />
      )}
    </>
  );
};

export default VideoThumbnail;
